import React from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { Container, Row, Col, Card, Button } from 'react-bootstrap';
import { useSite } from '../../context/SiteContext';
import { trackContactEvent } from '../../utils/trackingEvents';
import Header from '../Header';

const testimonials: Record<string, { name: string; text: string }[]> = {
  default: [
    { name: 'Maria S.', text: 'Atendimento incrível! Me senti acolhida desde a recepção. Resultado ficou melhor do que eu esperava.' },
    { name: 'Carlos R.', text: 'Profissionais muito competentes. Explicaram tudo com calma e o procedimento foi tranquilo.' },
    { name: 'Ana P.', text: 'Recomendo de olhos fechados. Minha autoestima mudou completamente depois do tratamento.' },
  ],
};

const ServiceLanding: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { config, serviceGroups, whatsappUrl, imageUrl } = useSite();

  const group = serviceGroups.find(g =>
    g.external_id === slug ||
    g.description.toLowerCase().replace(/\s+/g, '-').normalize('NFD').replace(/[\u0300-\u036f]/g, '') === slug
  );

  if (!group) return <Navigate to="/" replace />;

  const features: string[] = Array.isArray(group.features)
    ? group.features
    : (() => { try { return JSON.parse(group.features || '[]'); } catch { return []; } })();

  const handleCta = () => {
    trackContactEvent();
    window.open(whatsappUrl, '_blank');
  };

  const depoimentos = testimonials[group.description.toLowerCase()] || testimonials.default;

  return (
    <div className="landing-page">
      <Header />

      {/* Hero */}
      <section className="bg-profile-1 text-white py-5" style={{ minHeight: '55vh' }}>
        <Container className="d-flex align-items-center h-100">
          <Row className="w-100 align-items-center">
            <Col lg={7}>
              <span className="badge bg-profile-3 text-profile-1 mb-3 p-2">{config?.site_title || 'Sperandio Oral Center'}</span>
              <h1 className="display-4 fw-bold mb-3">
                {group.description} em Apucarana
              </h1>
              <p className="lead mb-4 text-white-50">
                {group.detailed_description || `Tratamento especializado em ${group.description.toLowerCase()} com tecnologia de ponta e equipe qualificada.`}
              </p>

              {features.length > 0 && (
                <div className="d-flex flex-wrap gap-2 mb-4">
                  {features.map((f, i) => (
                    <span key={i} className="badge bg-white bg-opacity-10 p-2">
                      <i className="fas fa-check-circle text-profile-3 me-1"></i>{f}
                    </span>
                  ))}
                </div>
              )}

              <div className="d-flex gap-3 flex-wrap">
                <Button variant="warning" size="lg" className="text-profile-1 fw-bold px-4" onClick={handleCta}>
                  <i className="far fa-calendar-alt me-2"></i>Agendar Avaliação
                </Button>
                <Button variant="outline-light" size="lg" className="px-4" onClick={handleCta}>
                  <i className="fab fa-whatsapp me-2"></i>Tire Suas Dúvidas
                </Button>
              </div>

              {config?.address && (
                <p className="mt-4 small text-white-50">
                  <i className="fas fa-map-marker-alt me-2"></i>{config.address}
                </p>
              )}
            </Col>
            <Col lg={5} className="text-center d-none d-lg-block">
              {group.photo?.image_path ? (
                <img src={imageUrl(group.photo.image_path)} alt={group.description} className="img-fluid rounded shadow" />
              ) : (
                <div className="bg-profile-3 bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center" style={{ width: '280px', height: '280px' }}>
                  <i className={`${group.icon_class || 'fas fa-tooth'} text-profile-3`} style={{ fontSize: '7rem' }}></i>
                </div>
              )}
            </Col>
          </Row>
        </Container>
      </section>

      {/* Barra de confiança */}
      <section className="bg-profile-3 py-3">
        <Container>
          <Row className="text-center">
            {[
              { icon: 'fas fa-shield-alt', label: 'Biossegurança Total' },
              { icon: 'fas fa-award', label: 'Especialistas Certificados' },
              { icon: 'fas fa-credit-card', label: 'Parcelamento em até 12x' },
              { icon: 'fas fa-clock', label: 'Atendimento Ágil' },
            ].map((item, i) => (
              <Col md={3} key={i} className="mb-2 mb-md-0">
                <i className={`${item.icon} me-2 text-profile-1`}></i>
                <span className="fw-bold text-profile-1 small">{item.label}</span>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* O que é + Como funciona */}
      <section className="py-5">
        <Container>
          <Row className="align-items-center">
            <Col lg={6} className="mb-4 mb-lg-0">
              <h2 className="display-6 fw-bold text-profile-1 mb-3">
                O que é {group.description}?
              </h2>
              <div className="bg-profile-3 mb-4" style={{ width: '80px', height: '4px' }}></div>
              <p className="text-profile-4 mb-3">
                {group.detailed_description || `${group.description} é um tratamento odontológico moderno que oferece resultados naturais e duradouros.`}
              </p>

              {features.length > 0 && (
                <ul className="list-unstyled mb-4">
                  {features.map((f, i) => (
                    <li key={i} className="mb-2 text-profile-4">
                      <i className="fas fa-check-circle text-profile-3 me-2"></i>{f}
                    </li>
                  ))}
                </ul>
              )}

              <Button variant="warning" size="lg" className="text-profile-1 fw-bold" onClick={handleCta}>
                <i className="fab fa-whatsapp me-2"></i>Agendar minha avaliação
              </Button>
            </Col>
            <Col lg={6}>
              <div className="bg-profile-1 text-white rounded p-4">
                <h5 className="fw-bold mb-4">
                  <i className="fas fa-list-check me-2 text-profile-3"></i>Como funciona?
                </h5>
                {[
                  { step: '1', title: 'Avaliação Personalizada', desc: 'Análise completa do seu caso com planejamento digital.' },
                  { step: '2', title: 'Planejamento do Tratamento', desc: 'Definimos juntos o melhor plano para o seu sorriso.' },
                  { step: '3', title: 'Resultado Transformador', desc: 'Saia com o sorriso que sempre sonhou.' },
                ].map((s, i) => (
                  <div className={`d-flex align-items-start ${i < 2 ? 'mb-3' : ''}`} key={i}>
                    <div className="bg-profile-3 rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0" style={{ width: '40px', height: '40px' }}>
                      <span className="fw-bold text-profile-1">{s.step}</span>
                    </div>
                    <div>
                      <h6 className="fw-bold mb-1">{s.title}</h6>
                      <p className="text-white-50 small mb-0">{s.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Diferenciais */}
      <section className="py-5 bg-profile-5">
        <Container>
          <Row className="text-center mb-5">
            <Col>
              <h2 className="display-6 fw-bold text-profile-1 mb-3">Por que fazer {group.description} na {config?.site_title || 'nossa clínica'}?</h2>
              <div className="bg-profile-3 mx-auto" style={{ width: '80px', height: '4px' }}></div>
            </Col>
          </Row>
          <Row className="g-4">
            {[
              { icon: 'fas fa-microscope', title: 'Tecnologia de Ponta', text: 'Equipamentos modernos para diagnósticos precisos e tratamentos eficazes.' },
              { icon: 'fas fa-user-md', title: 'Especialistas Qualificados', text: 'Equipe com formação específica e anos de experiência comprovada.' },
              { icon: 'fas fa-gem', title: 'Materiais Premium', text: 'Utilizamos apenas materiais certificados de alta durabilidade e estética natural.' },
              { icon: 'fas fa-heart', title: 'Atendimento Humanizado', text: 'Cuidamos de você com atenção, respeito e conforto em cada etapa.' },
              { icon: 'fas fa-shield-alt', title: 'Segurança e Biossegurança', text: 'Protocolos rigorosos de esterilização e controle de infecção.' },
              { icon: 'fas fa-credit-card', title: 'Facilidade de Pagamento', text: 'Parcelamento em até 12x no cartão. Condições especiais para você.' },
            ].map((item, i) => (
              <Col md={4} key={i}>
                <Card className="h-100 border-0 shadow-sm hover-card">
                  <Card.Body className="p-4 text-center">
                    <div className="bg-profile-1 rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: '70px', height: '70px' }}>
                      <i className={`${item.icon} fs-3 text-profile-3`}></i>
                    </div>
                    <Card.Title className="text-profile-1 fw-bold">{item.title}</Card.Title>
                    <Card.Text className="text-muted">{item.text}</Card.Text>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Pagamento + Garantia */}
      <section className="py-5 bg-profile-1 text-white">
        <Container>
          <Row className="align-items-center">
            <Col lg={6} className="mb-4 mb-lg-0">
              <h2 className="display-6 fw-bold mb-3">Sua segurança é nossa prioridade</h2>
              <div className="bg-profile-3 mb-4" style={{ width: '80px', height: '4px' }}></div>
              <p className="text-white-50 mb-4">
                {config?.site_title || 'Nossa clínica'} conta com mais de {config?.founded_year ? new Date().getFullYear() - parseInt(config.founded_year) : 15} anos de experiência,
                milhares de pacientes atendidos e uma equipe em constante atualização profissional.
              </p>
              {[
                'Protocolos rigorosos de biossegurança',
                'Materiais certificados pela ANVISA',
                'Equipe com especializações reconhecidas',
                'Acompanhamento pós-tratamento',
              ].map((item, i) => (
                <div className="d-flex align-items-center mb-2" key={i}>
                  <div className="bg-profile-3 bg-opacity-25 rounded-circle p-2 me-2">
                    <i className="fas fa-check text-profile-3"></i>
                  </div>
                  <span className="small">{item}</span>
                </div>
              ))}
            </Col>
            <Col lg={6}>
              <Card className="border-0 shadow bg-white text-dark">
                <Card.Body className="p-4 text-center">
                  <i className="fas fa-credit-card fs-1 text-profile-3 mb-3"></i>
                  <h4 className="text-profile-1 fw-bold mb-3">Condições Especiais de Pagamento</h4>
                  <p className="text-muted mb-3">Facilitamos para que você realize o tratamento que precisa, sem pesar no bolso.</p>
                  <div className="bg-profile-5 rounded p-3 mb-3">
                    <span className="display-6 fw-bold text-profile-1">até 12x</span>
                    <p className="text-muted small mb-0">no cartão de crédito</p>
                  </div>
                  <p className="text-muted small mb-3">Aceitamos cartão, PIX.</p>
                  <Button variant="warning" size="lg" className="w-100 text-profile-1 fw-bold" onClick={handleCta}>
                    <i className="fab fa-whatsapp me-2"></i>Agendar uma Avaliação
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Depoimentos */}
      <section className="py-5 bg-profile-5">
        <Container>
          <Row className="text-center mb-5">
            <Col>
              <h2 className="display-6 fw-bold text-profile-1 mb-3">O que nossos pacientes dizem</h2>
              <div className="bg-profile-3 mx-auto" style={{ width: '80px', height: '4px' }}></div>
            </Col>
          </Row>
          <Row className="g-4">
            {depoimentos.map((t, i) => (
              <Col md={4} key={i}>
                <Card className="h-100 border-0 shadow-sm">
                  <Card.Body className="p-4">
                    <div className="d-flex mb-3">
                      {[...Array(5)].map((_, s) => <i key={s} className="fas fa-star text-warning me-1"></i>)}
                    </div>
                    <Card.Text className="text-profile-4 fst-italic mb-3">"{t.text}"</Card.Text>
                    <div className="d-flex align-items-center">
                      <div className="bg-profile-1 rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '45px', height: '45px' }}>
                        <i className="fas fa-user text-profile-3"></i>
                      </div>
                      <div>
                        <h6 className="text-profile-1 fw-bold mb-0">{t.name}</h6>
                        <small className="text-muted">Paciente — {group.description}</small>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* CTA Final */}
      <section className="py-5 bg-profile-1 text-white">
        <Container>
          <Row className="text-center">
            <Col lg={8} className="mx-auto">
              <h2 className="display-5 fw-bold mb-3">Pronto para transformar seu sorriso?</h2>
              <p className="lead text-white-50 mb-4">
                Agende sua avaliação de {group.description.toLowerCase()} agora mesmo pelo WhatsApp. Atendimento rápido e sem compromisso.
              </p>
              <Button variant="warning" size="lg" className="text-profile-1 fw-bold px-5 mb-2" onClick={handleCta}>
                <i className="fab fa-whatsapp me-2"></i>Quero meu novo sorriso agora
              </Button>
              {config?.phone && (
                <p className="mt-3 text-white-50">
                  Ou ligue: <a href={`tel:${config.phone.replace(/\D/g, '')}`} className="text-profile-3 text-decoration-none fw-bold">{config.phone}</a>
                </p>
              )}
            </Col>
          </Row>
        </Container>
      </section>

      {/* Mini Footer */}
      <footer className="bg-profile-1 text-white-50 py-3 border-top border-white border-opacity-10">
        <Container>
          <Row className="text-center">
            <Col>
              <small>&copy; {new Date().getFullYear()} {config?.site_title} {config?.cnpj && <>| CNPJ: {config.cnpj}</>}</small>
            </Col>
          </Row>
        </Container>
      </footer>

      <style>{`
        .hover-card { transition: transform 0.3s ease, box-shadow 0.3s ease; }
        .hover-card:hover { transform: translateY(-5px); box-shadow: 0 10px 30px rgba(20,63,63,0.2); }
      `}</style>
    </div>
  );
};

export default ServiceLanding;
